import { TextField, Tooltip } from '@mui/material'
import { useEffect, useState } from 'react'

const textFieldStyle = {
  minWidth: '5rem'
}

/**
 * Generic component for text fields
 * @param {Object} props
 * @param {string} props.label Label to show
 * @param {boolean} props.shrink  If true, the label will be displayed in an compressed form (default = true)
 * @param {string} props.value Value of the text field
 * @param {import('react').HTMLInputTypeAttribute} props.type Type of the text field
 * @param {function(string): void} props.onChange  Function to call when the value of the text field changes
 * @param {function(string): string } props.checkError Function to call to check if the value of the text field is valid
 * @param {boolean} props.forceCheckError On value change, check if the value of the text field is valid
 * @param {boolean} props.showErrorAsCompress If true, the error message will be shown in an compressed form (default = true)
 * @param {import('react').CSSProperties} props.style Style of the text field (default = textFieldStyle)
 * @param {boolean} props.disabled If true, component will be disabled
 * @param {boolean} props.required If true, component will be required
 * @param {boolean} props.checkAuto If true, component will check the value on change (default = false)
 * @returns
 */
const SmartTextField = (props) => {
  const { label, shrink = true, value, type, onChange, checkError, forceCheckError = false, showErrorAsCompress = true, style = textFieldStyle, disabled, required, checkAuto = false, ...other } = props
  const [errorMessage, setErrorMessage] = useState('')
  const hasError = Boolean(errorMessage)

  useEffect(() => {
    if (!forceCheckError || !checkError) return
    setErrorMessage(checkError(value))
  }, [forceCheckError, value, checkError])

  useEffect(() => {
    const check = () => {
      setErrorMessage(checkError(value))
    }
    if (!checkError || !checkAuto) {
      return
    }
    check()
  }, [forceCheckError, value, checkError, checkAuto])

  return (
    <TextField
      {...other}
      type={type}
      InputLabelProps={{
        shrink
      }}
      style={style}
      label={hasError
        ? showErrorAsCompress
          ? (
            <Tooltip placement='top' title={errorMessage}>
              {' ' + label}
            </Tooltip>
            )
          : errorMessage
        : label}
      value={value}
      onChange={(event) => {
        if (checkError) {
          setErrorMessage(checkError(event.target.value))
        }
        onChange(event.target.value)
      }}
      error={hasError}
      disabled={disabled}
      required={required}
    />
  )
}

export default SmartTextField
